import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../components/Toast';
import { useSession, signOut } from '../lib/session';
import { supabase } from '../lib/supabase';
import {
  updateOwnProfile,
  uploadProfileImage,
  fetchMyPaymentMethod,
  removeMyPaymentMethod,
  type ProfileEditPatch,
} from '../lib/queries';
import type { MemberPaymentMethod } from '../lib/types';
import { AddCardModal } from '../components/AddCardModal';
import { withTimeout } from '../lib/timeout';
import { checkHandleAvailable } from '../lib/onboarding-queries';
import {
  SOCIAL_PLATFORMS,
  resolveSocialEntries,
  type SocialPlatformId,
} from '../lib/socials';
import { countryName } from '../lib/countries';
import { CountrySelect } from '../components/CountrySelect';
import { FlagIcon } from '../components/FlagIcon';
import './Profile.css';

/* Size a textarea to its content so the Bio / treasured-garment boxes always
   show the whole text — runs once on mount (ref) and again on every input. */
function autoGrowTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + 2}px`;
}

export default function Profile() {
  const { profile, loading } = useSession();
  const [edit, setEdit] = useState(false);
  const toast = useToast();

  if (loading) {
    return (
      <div className="profile profile--me" aria-busy="true" aria-label="Loading profile">
        <aside className="profile__stripe" aria-hidden="true">
          <ProfileStripeLogo />
        </aside>
        <main className="profile__pane container">
          <div className="profile__pane-inner">
            <div className="profile__identity">
              <Skeleton variant="avatar" width={112} height={112} />
              <div style={{ height: 'var(--space-4)' }} />
              <Skeleton variant="text" width={220} height={28} />
              <div style={{ height: 'var(--space-2)' }} />
              <Skeleton variant="text" width="40%" />
            </div>
          </div>
        </main>
      </div>
    );
  }
  if (!profile) return null;

  const founding = profile.is_founding_member ?? false;
  const socials = resolveSocialEntries(profile.social_links, profile.instagram_handle);

  return (
    <div className="profile profile--me">
      <aside className="profile__stripe" aria-hidden="true">
        <ProfileStripeLogo />
      </aside>

      <main className="profile__pane container">
        <div className="profile__pane-inner">
          <p className="kicker profile__eyebrow">Your closet profile</p>

          {!edit ? (
            <section className="profile__identity">
              <div className="profile__identity-head">
                <Avatar
                  name={profile.display_name || 'You'}
                  src={profile.avatar_url}
                  size="lg"
                  founding={founding}
                />
                <div className="profile__identity-text">
                  <h1 className="profile__name">{profile.display_name || 'Add your name'}</h1>
                  {profile.handle && (
                    <p className="profile__handle">@{profile.handle}</p>
                  )}
                  {(founding || profile.role !== 'user') && (
                    <div className="profile__tags">
                      {founding && <span className="profile__tag">Founding Member</span>}
                      {profile.role !== 'user' && (
                        <span className="profile__tag profile__tag--role">{profile.role}</span>
                      )}
                    </div>
                  )}
                  {(profile.location || profile.country) && (
                    <p className="profile__location-line">
                      <FlagIcon code={profile.country} />{' '}
                      {[profile.location, countryName(profile.country)].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Bio → Fabric selection → Edit profile → rule → social links.
                  The unset prompts nudge members toward fuller profiles. */}
              <div className="profile__section">
                <p className="kicker">Bio</p>
                {profile.bio ? (
                  <p className="profile__bio">{profile.bio}</p>
                ) : (
                  <p className="profile__bio profile__bio--prompt">
                    Tell us about yourself and why you're here!
                  </p>
                )}
              </div>

              {/* Optional — when unset the row disappears entirely (no
                  empty placeholder), per the team's profile-page pass. */}
              {profile.fabric_selection && (
                <div className="profile__section">
                  <p className="kicker">Most treasured garment</p>
                  <p className="profile__bio">{profile.fabric_selection}</p>
                </div>
              )}

              {/* Edit profile is the LAST thing before the full rule above
                  Social profiles, right-aligned on the same axis as Sign out. */}
              <div className="profile__actions">
                <button
                  type="button"
                  className="btn btn--primary btn--md"
                  onClick={() => setEdit(true)}
                >
                  Edit profile
                </button>
              </div>
            </section>
          ) : (
            <EditPanel onClose={() => setEdit(false)} />
          )}

          <hr className="profile__rule" />

          {/* Vertical flow (team request): Bio → garment → Edit profile →
              rule → SOCIAL PROFILES → rule → Billing. Hidden while editing,
              like the rest of the read view. */}
          {!edit && socials.length > 0 && (
            <>
              <div className="profile__section">
                <p className="kicker">Social profiles</p>
                <ul className="profile__socials" aria-label="Social profiles">
                  {socials.map(({ platform, value }) => (
                    <li key={platform.id}>
                      <a
                        className="profile__social"
                        href={platform.urlFor(value)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={platform.label}
                        title={platform.label}
                      >
                        <span className="profile__social-box"><platform.Icon /></span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <hr className="profile__rule" />
            </>
          )}

          <BillingSection userId={profile.id} />
          <hr className="profile__rule" />
          <AuctionPaymentSection />

          <hr className="profile__rule" />

          <footer className="profile__signout">
            <button type="button" className="btn btn--primary btn--sm" onClick={signOut}>
              Sign out
            </button>
          </footer>
        </div>
      </main>
    </div>
  );
}

export function ProfileStripeLogo() {
  return (
    <div className="profile__logo">
      <img className="profile__logo-ring" src="/logos/cc-logo-icon-white.png" alt="" />
      <span className="profile__logo-wordmark">
        <img src="/logos/cc-logo-full-white.png" alt="" />
      </span>
    </div>
  );
}

interface SubRow {
  status: 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'comped';
  plan: string | null;
  tier: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
}

// Bidding card on file (member_payment_methods). Lets a member view, update,
// or remove the card the place_bid gate requires. Shown to the same audience
// as BillingSection (non-staff, including comped/founding members who can bid).
function AuctionPaymentSection() {
  const toast = useToast();
  const [pm, setPm] = useState<MemberPaymentMethod | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [removing, setRemoving] = useState(false);

  function load() {
    return fetchMyPaymentMethod()
      .then((p) => setPm(p))
      .catch(() => { /* leave as-is */ })
      .finally(() => setLoaded(true));
  }
  useEffect(() => { void load(); }, []);

  async function remove() {
    if (!window.confirm("Remove your saved bidding card? You'll need to add one again before bidding.")) return;
    setRemoving(true);
    try {
      await removeMyPaymentMethod();
      toast.success('Card removed.');
      setPm(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not remove the card.');
    } finally {
      setRemoving(false);
    }
  }

  const hasCard = !!pm?.has_card_on_file;
  const brand = pm?.card_brand ? pm.card_brand.charAt(0).toUpperCase() + pm.card_brand.slice(1) : 'Card';
  const expiry = pm?.card_exp_month
    ? ` · exp ${String(pm.card_exp_month).padStart(2, '0')}/${String(pm.card_exp_year ?? '').slice(-2)}`
    : '';

  return (
    <section className="profile__section">
      <p className="kicker">Auction payments</p>
      {!loaded ? (
        <Skeleton variant="text" width="60%" />
      ) : hasCard ? (
        <>
          <p className="profile__section-body">
            {brand} ···· {pm?.card_last4 ?? '••••'}{expiry}
          </p>
          <div className="profile__section-actions">
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setShowAdd(true)} disabled={removing}>
              Update card
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={remove} disabled={removing}>
              {removing ? '…' : 'Remove card'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="profile__section-body">
            No bidding card on file. Add one to place bids in the Sales Room; you're only charged if you win.
          </p>
          <button type="button" className="btn btn--accent btn--sm" onClick={() => setShowAdd(true)}>
            Add a card
          </button>
        </>
      )}
      {showAdd && <AddCardModal onClose={() => setShowAdd(false)} onLinked={() => { void load(); }} />}
    </section>
  );
}

function BillingSection({ userId }: { userId: string }) {
  const toast = useToast();
  const [sub, setSub] = useState<SubRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [openingPortal, setOpeningPortal] = useState<null | 'manage' | 'switch_annual' | 'cancel'>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, plan, tier, current_period_end, cancel_at_period_end, stripe_customer_id')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn('[profile/billing] subscription fetch failed', error);
        setLoaded(true);
        return;
      }
      setSub(data as SubRow | null);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (!loaded) {
    return (
      <section className="profile__section">
        <p className="kicker">Billing</p>
        <Skeleton variant="text" width="60%" />
      </section>
    );
  }

  if (sub?.status === 'comped') {
    return (
      <section className="profile__section">
        <p className="kicker">Billing</p>
        <p className="profile__section-body">
          Complimentary Access. Thank you for being part of the Club.
        </p>
      </section>
    );
  }

  if (!sub || sub.status === 'none' || (sub.status === 'canceled' && !sub.stripe_customer_id)) {
    return (
      <section className="profile__section">
        <p className="kicker">Billing</p>
        <p className="profile__section-body">
          You don't have an active membership.
        </p>
        <Link to="/subscribe" className="btn btn--accent btn--sm">
          Join the Club
        </Link>
      </section>
    );
  }

  const planLabel = sub.plan === 'annual' ? 'Annual' : sub.plan === 'monthly' ? 'Monthly' : (sub.plan ?? '—');
  const renewalDate = sub.current_period_end
    ? new Date(sub.current_period_end).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : null;

  async function openPortal(flow: 'manage' | 'switch_annual' | 'cancel') {
    setOpeningPortal(flow);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Sign in first.');
      const flowParam =
        flow === 'switch_annual' ? 'subscription_update' :
        flow === 'cancel'        ? 'subscription_cancel' :
                                    undefined;
      const res = await fetch('/api/stripe/portal', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(flowParam ? { flow: flowParam } : {}),
      });
      const data = await res.json();
      if (!res.ok || !data?.url) throw new Error(data?.error ?? 'Could not open billing portal.');
      window.location.assign(data.url);
    } catch (err: any) {
      toast.error(err?.message ?? 'Portal failed.');
      setOpeningPortal(null);
    }
  }

  if (sub.status === 'past_due') {
    return (
      <section className="profile__section">
        <p className="kicker" style={{ color: 'var(--accent)' }}>Payment failed</p>
        <p className="profile__section-body">
          Your last payment didn't go through. Update your card to keep access.
        </p>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => openPortal('manage')}
          disabled={openingPortal !== null}
        >
          {openingPortal === 'manage' ? '…' : 'Update payment method'}
        </button>
      </section>
    );
  }

  return (
    <section className="profile__section">
      <p className="kicker">Billing</p>
      <p className="profile__section-body">
        Conversion Club - {planLabel}
        {sub.cancel_at_period_end && renewalDate && (
          <> · ending {renewalDate}</>
        )}
        {!sub.cancel_at_period_end && renewalDate && (
          <> · renews {renewalDate}</>
        )}
      </p>
      <div className="profile__section-actions">
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => openPortal('manage')}
          disabled={openingPortal !== null}
        >
          {openingPortal === 'manage' ? '…' : 'Manage billing'}
        </button>
        {sub.plan === 'monthly' && !sub.cancel_at_period_end && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => openPortal('switch_annual')}
            disabled={openingPortal !== null}
            title="Switch to annual billing"
          >
            {openingPortal === 'switch_annual' ? '…' : 'Switch to annual'}
          </button>
        )}
      </div>
    </section>
  );
}

function EditPanel({ onClose }: { onClose: () => void }) {
  const { profile } = useSession();
  const toast = useToast();
  const [displayName, setDisplayName] = useState(profile?.display_name ?? '');
  const [handle, setHandle] = useState(profile?.handle ?? '');
  const [location, setLocation] = useState(profile?.location ?? '');
  const [country, setCountry] = useState(profile?.country ?? '');
  const [bio, setBio] = useState(profile?.bio ?? '');
  const [fabric, setFabric] = useState(profile?.fabric_selection ?? '');
  // Socials are stored as a {platform_id: handle} map containing ONLY the
  // platforms the user has chosen to add. Adding a platform inserts an
  // empty-string entry; removing drops the entry entirely. This drives
  // both the rendered input rows and the contents of the "Add platform"
  // dropdown (which lists platforms not currently in the map).
  const [socials, setSocials] = useState<Partial<Record<SocialPlatformId, string>>>(() => {
    const out: Partial<Record<SocialPlatformId, string>> = {};
    const stored = profile?.social_links ?? {};
    for (const p of SOCIAL_PLATFORMS) {
      const v = stored[p.id];
      if (typeof v === 'string' && v.trim()) out[p.id] = v;
    }
    // Pull the legacy column in if social_links doesn't already carry IG.
    if (!out.instagram && profile?.instagram_handle) {
      out.instagram = profile.instagram_handle.replace(/^@+/, '');
    }
    return out;
  });
  const addedPlatforms = SOCIAL_PLATFORMS.filter((p) => p.id in socials);
  const availablePlatforms = SOCIAL_PLATFORMS.filter((p) => !(p.id in socials));
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url ?? '');
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarInput = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Password change
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);

  if (!profile) return null;

  async function handleUpload(file: File | null) {
    const p = profile;
    if (!file || !p) return;
    setAvatarBusy(true);
    try {
      const url = await uploadProfileImage({ userId: p.id, kind: 'avatar', file });
      setAvatarUrl(url);
      toast.success('Avatar uploaded.');
    } catch (err: any) {
      console.error('[profile] upload failed', err);
      toast.error(err?.message ?? 'Upload failed');
    } finally {
      setAvatarBusy(false);
    }
  }

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    // Sanitize each social value through its platform's rule, drop blanks,
    // and emit a clean Record. IG also writes through to the legacy
    // instagram_handle column so older surfaces keep working until they're
    // migrated over.
    const cleanSocials: Record<string, string> = {};
    for (const platform of SOCIAL_PLATFORMS) {
      const raw = (socials[platform.id] ?? '').trim();
      if (!raw) continue;
      const clean = platform.sanitize(raw);
      if (clean) cleanSocials[platform.id] = clean;
    }
    const patch: ProfileEditPatch = {
      display_name: displayName.trim(),
      handle: handle.trim() || null,
      location: location.trim() || null,
      country: country || null,
      bio: bio.trim() || null,
      fabric_selection: fabric.trim() || null,
      instagram_handle: cleanSocials.instagram ?? null,
      social_links: cleanSocials,
      avatar_url: avatarUrl || null,
    };
    try {
      // Handles are unique club-wide. Verify availability BEFORE writing so a
      // clash reads as a clear message, not a raw constraint error. The check
      // treats the user's own current handle as available.
      if (patch.handle) {
        const free = await withTimeout(
          checkHandleAvailable(patch.handle),
          10000,
          'Handle check',
        );
        if (!free) {
          const taken = 'That @handle is already taken — try another.';
          setMsg(taken);
          toast.error(taken);
          setSaving(false);
          return;
        }
      }
      await withTimeout(updateOwnProfile(profile!.id, patch), 15000, 'Save');
      toast.success('Profile saved.');
      setSaving(false);
      // Full reload so SessionProvider re-fetches the profile + every
      // surface (nav avatar, comment author cards, banner) picks the new
      // values up at once.
      setTimeout(() => window.location.reload(), 600);
      return;
    } catch (err: any) {
      console.error('[profile] save failed', err);
      const m = err?.message ?? '';
      // The unique-violation backstop catches the race where someone claims
      // the handle between our availability check and the write.
      const friendly = /handle_taken|duplicate key|profiles_handle_key/i.test(m)
        ? 'That @handle is already taken — try another.'
        : /invalid_handle/.test(m)
          ? 'Handle must be 2–30 chars: a–z, 0–9, underscore.'
          : m || 'Save failed';
      setMsg(friendly);
      toast.error(friendly);
    }
    setSaving(false);
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwMsg(null);
    if (pw1.length < 8) { setPwMsg('At least 8 characters.'); return; }
    if (pw1 !== pw2) { setPwMsg("Passwords don't match."); return; }
    setPwBusy(true);
    try {
      const { error } = await withTimeout(
        supabase.auth.updateUser({ password: pw1 }),
        15000,
        'Password update',
      );
      if (error) throw error;
      setPw1('');
      setPw2('');
      toast.success('Password updated.');
    } catch (err: any) {
      setPwMsg(err?.message ?? 'Update failed');
      toast.error(err?.message ?? 'Password update failed.');
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <section className="profile__edit">
      <form className="profile__form" onSubmit={saveProfile}>
        <section className="profile__card">
          <div className="profile__form-row">
            <label className="profile__field">
              <span>Name</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={80}
                autoComplete="name"
                placeholder="Your name"
              />
            </label>
            <label className="profile__field">
              <span>Handle</span>
              <div className="profile__handle-wrap">
                <span className="profile__handle-at" aria-hidden="true">@</span>
                <input
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  maxLength={30}
                  placeholder="handle"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
            </label>
          </div>

          <div className="profile__field">
            <span>Location</span>
            <div className="profile__loc-row">
              <CountrySelect value={country} onChange={setCountry} className="profile__select" compact />
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                maxLength={80}
                placeholder="City / area"
                autoComplete="address-level2"
                aria-label="City or area"
              />
            </div>
          </div>

          <label className="profile__field">
            <span>Bio</span>
            <textarea
              ref={autoGrowTextarea}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              onInput={(e) => autoGrowTextarea(e.currentTarget)}
              maxLength={500}
              rows={3}
              placeholder="Tell us about yourself and why you're here!"
            />
          </label>

          <label className="profile__field">
            <span>Most treasured garment</span>
            <textarea
              ref={autoGrowTextarea}
              value={fabric}
              onChange={(e) => setFabric(e.target.value)}
              onInput={(e) => autoGrowTextarea(e.currentTarget)}
              maxLength={300}
              rows={2}
              placeholder="Tell us all about your garment!"
            />
          </label>
        </section>

        <fieldset className="profile__card profile__socials-edit">
          {/* The visible title lives OUTSIDE the legend: rendered legends sit
              outside the fieldset's flex flow, so they ignore the card gap and
              break the uniform title→content rhythm. */}
          <legend className="visually-hidden">Social links</legend>
          <div className="profile__socials-head">
            <span className="profile__field-label">Social links</span>
            <span className="profile__form-hint">
              Add the platforms you want shown on your profile. Each one
              becomes an icon people can click through.
            </span>
          </div>

          {addedPlatforms.length > 0 && (
            <ul className="profile__social-rows">
              {addedPlatforms.map((platform) => (
                <li key={platform.id} className="profile__social-input">
                  <span className="profile__social-input-icon" aria-hidden="true">
                    <platform.Icon />
                  </span>
                  <label className="visually-hidden" htmlFor={`social-${platform.id}`}>
                    {platform.label}
                  </label>
                  <div
                    className={`profile__social-input-wrap${platform.prefix ? ' profile__social-input-wrap--at' : ''}`}
                  >
                    {platform.prefix && (
                      <span className="profile__social-at" aria-hidden="true">{platform.prefix}</span>
                    )}
                    <input
                      id={`social-${platform.id}`}
                      value={socials[platform.id] ?? ''}
                      onChange={(e) =>
                        setSocials((s) => ({ ...s, [platform.id]: e.target.value }))
                      }
                      maxLength={200}
                      placeholder={platform.placeholder}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>
                  <button
                    type="button"
                    className="profile__social-remove"
                    onClick={() =>
                      setSocials((s) => {
                        const next = { ...s };
                        delete next[platform.id];
                        return next;
                      })
                    }
                    aria-label={`Remove ${platform.label}`}
                    title={`Remove ${platform.label}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {availablePlatforms.length > 0 && (
            <div className="profile__social-add">
              <label htmlFor="social-add">
                <span className="visually-hidden">Add a platform</span>
              </label>
              <select
                id="social-add"
                value=""
                onChange={(e) => {
                  const id = e.target.value as SocialPlatformId | '';
                  if (!id) return;
                  setSocials((s) => ({ ...s, [id]: '' }));
                }}
              >
                <option value="">+ Add a platform…</option>
                {availablePlatforms.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </fieldset>

        <section className="profile__card profile__avatar-edit">
          <p className="kicker">Avatar</p>
          <div className="profile__avatar-edit-row">
            <span
              className={
                'profile__avatar-edit-circle' +
                (avatarBusy ? ' profile__avatar-edit-circle--busy' : '')
              }
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" />
              ) : (
                <Avatar
                  name={profile.display_name || 'You'}
                  size="lg"
                  founding={profile.is_founding_member ?? false}
                />
              )}
            </span>
            <div className="profile__avatar-edit-actions">
              <input
                ref={avatarInput}
                type="file"
                accept="image/*"
                className="visually-hidden"
                onChange={(e) => {
                  handleUpload(e.target.files?.[0] ?? null);
                  e.currentTarget.value = '';
                }}
              />
              <button
                type="button"
                className="btn btn--secondary btn--sm profile__media-btn"
                onClick={() => avatarInput.current?.click()}
                disabled={avatarBusy}
              >
                {avatarBusy ? 'Uploading…' : avatarUrl ? 'Replace' : 'Upload'}
              </button>
              <p className="profile__form-hint">Square JPG or PNG.</p>
            </div>
          </div>
        </section>

        {msg && <p className="profile__form-msg">{msg}</p>}

        <div className="profile__form-actions">
          {/* Cancel rides on the same row as the save action (team request). */}
          <button type="button" className="btn btn--ghost btn--md" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary btn--md" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      <form className="profile__form profile__card profile__form--pw" onSubmit={changePassword}>
        <header className="profile__form-head">
          <p className="kicker">Change password</p>
          <p className="profile__form-hint">Sets a password on your account so you can sign in without a one time link.</p>
        </header>
        <div className="profile__form-row">
          <label className="profile__field">
            <span>New password</span>
            <input
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              minLength={8}
              autoComplete="new-password"
            />
          </label>
          <label className="profile__field">
            <span>Confirm</span>
            <input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              minLength={8}
              autoComplete="new-password"
            />
          </label>
        </div>
        {pwMsg && <p className="profile__form-msg">{pwMsg}</p>}
        <div className="profile__form-actions">
          <button type="submit" className="btn btn--secondary btn--md" disabled={pwBusy || !pw1 || !pw2}>
            {pwBusy ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </section>
  );
}

